import React, { Fragment, useEffect, useState, useContext } from 'react';
import VoucherCartItem from './VoucherCartItem'
import ResdiaryTermsAndConditions from './ResdiaryTermsAndConditions';
import ValidationInput from './ValidationInput';
import { injectIntl } from 'react-intl';
import { Input, Label, Button, FormGroup } from 'reactstrap';
import SegmentHelper from '../helpers/SegmentHelper'
import useScreenSize from '../hooks/useScreenSize'
import { FormattedMessage } from 'react-intl';
import { ReactComponent as ChevronLeftIcon } from '../content/icons/chevron-left.svg';
import ButtonWrapper from './Button';
import SetupContext from './../contexts/SetupContext';
import ContactDetails from './../domainObjects/ContactDetails';
import deliveryMethod from './../enums/deliveryMethod';
import sendingOption from './../enums/sendingOption';
import VoucherService from '../services/VoucherService';
import CartHelper from '../helpers/CartHelper';
import { loadStripe } from '@stripe/stripe-js';
import RestaurantTermsAndConditions from './RestaurantTermsAndConditions.js';
import ValidationHelper from '../helpers/ValidationHelper';

const VoucherCartPage = (props) => {

    const { intl } = props;
    const setupContext = useContext(SetupContext);

    const [termsAndConditionsAccepted, setTermsAndConditionsAccepted] = useState(false);
    const [venueTermsAndConditionsAccepted, setVenueTermsAndConditionsAccepted] = useState(false);
    const [stripe, setStripe] = useState(null);
    const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
    const location = "Voucher cart";
    const [contactDetails, setContactDetails] = useState(new ContactDetails());
    const { isTabletView } = useScreenSize();

    useEffect(() => {
        if (!setupContext.vouchers) {
            props.history.push(`/${props.microsite}/`);
        }
        SegmentHelper.sendPageLoad(location);
    }, [location, setupContext]);

    useEffect(() => {
        async function fetchStripe() {

            let stripePromise;
            if (setupContext.stripeAccountId) {
                stripePromise = await loadStripe(setupContext.stripePublishableKey,
                    {
                        stripeAccount: setupContext.stripeAccountId
                    });
            } else {
                stripePromise = await loadStripe(setupContext.stripePublishableKey);
            }
            setStripe(stripePromise);
        }

        if (!stripe && setupContext.paymentProvider !== 'GuestPay') {
            fetchStripe();
        }
    }, [setupContext]);

    // Sync billing contact details to recipient details for "send to me" email delivery items
    // This eliminates the race condition where recipient details were only copied at checkout time
    // Only sync for items that were added to cart with no recipient details (needsBillingSync flag)
    useEffect(() => {
        if (contactDetails.firstName && contactDetails.lastName && contactDetails.email) {
            const updatedOrderItems = props.orderItems.map(ci => {
                // Only sync for items flagged as needing billing-to-recipient sync
                // This flag is set when the item is added to cart with no recipient details
                if (ci.sendingOption === sendingOption.sendToMe &&
                    ci.deliveryMethod === deliveryMethod.email &&
                    ci.needsBillingSync) {
                    return {
                        ...ci,
                        recipientDetails: {
                            ...ci.recipientDetails,
                            firstName: contactDetails.firstName,
                            lastName: contactDetails.lastName,
                            email: contactDetails.email
                        }
                    };
                }
                return ci;
            });

            // Only update if something actually changed to avoid infinite loops
            if (JSON.stringify(updatedOrderItems) !== JSON.stringify(props.orderItems)) {
                props.setOrderItems(updatedOrderItems);
            }
        }
    }, [contactDetails.firstName, contactDetails.lastName, contactDetails.email]);
    
    function setContactDetailsField(fieldName, value) {
        const contactDetailsCopy = { ...contactDetails };
        const sanitizedValue = fieldName === 'email'
            ? ValidationHelper.sanitizeEmail(value)
            : value;
        contactDetailsCopy[fieldName] = sanitizedValue;
        setContactDetails(contactDetailsCopy);
    }

    function updateCartItemQuantity(quantity, cartItemUniqueId) {
        const cartItem = props.orderItems.find(ci => ci.uniqueIdentifier === cartItemUniqueId);
        if (quantity < cartItem.minimumQuantity) {
            props.removeOrderItem(cartItem.cartItemUniqueId);
        } else {
            cartItem.quantity = quantity;
            props.updateOrderItem(cartItem);
        }
    }


    function getAnalyticsProperties(voucherDtos) {
        let properties = {};
        voucherDtos.forEach((voucher, index) => {
            properties['Voucher_name' + (index + 1)] = voucher.name;
            properties['Voucher_cost' + (index + 1)] = voucher.cost;
            properties['Voucher_quantity' + (index + 1)] = voucher.quantity;
        });
        return { vouchers: properties, total_cost: props.totalCost };
    }

    async function redirectToCheckout() {
        if (!stripe && setupContext.paymentProvider !== 'GuestPay') {
            return;
        }
        setRedirectingToCheckout(true);
        const host = (new URL(window.location.href)).host;

        SegmentHelper.sendButtonClicked("Pay with card");

        const postageMethods = [];
        const vouchersToOrder = [];

        props.orderItems.forEach(ci => {
            if (ci.sendingOption === sendingOption.sendToMe) {
                // Recipient details for "send to me" items are now synced via useEffect
                // when billing contact details change, eliminating the previous race condition
                const matchingItemInCart = CartHelper.findMatchingItemInCart(props.orderItems, ci, true);
                if (matchingItemInCart) {
                    // If item with same delivery details exists in cart, increase quantity of that item
                    if (matchingItemInCart.maximumQuantity >= matchingItemInCart.quantity + ci.quantity) {
                        matchingItemInCart.quantity = matchingItemInCart.quantity + ci.quantity;
                    }
                } else {
                    if (ci.deliveryDetails && ci.deliveryDetails.deliveryOption && ci.deliveryDetails.chargeForDelivery) {
                        postageMethods.push(ci.deliveryDetails.deliveryOption);
                    }
                    vouchersToOrder.push(ci);
                }
            } else {
                if (ci.deliveryDetails && ci.deliveryDetails.deliveryOption && ci.deliveryDetails.chargeForDelivery) {
                    postageMethods.push(ci.deliveryDetails.deliveryOption);
                }
                vouchersToOrder.push(ci);
            }
        });

        SegmentHelper.trackClickWithProperties("Web Voucher Pay", getAnalyticsProperties(vouchersToOrder));

        if (setupContext.useVoucherWebhooks) {
            const voucherDto = createRequestObject(vouchersToOrder, contactDetails, host, postageMethods);
            VoucherService.generateVoucherCheckoutSessionWithAutoCapture(props.microsite, voucherDto).then(async function (sessionId) {
                storeCartInfoInLocalStorage(sessionId, vouchersToOrder);
                if (setupContext.paymentProvider === 'GuestPay') {
                    window.sessionStorage.setItem('guestpay_pending_session', sessionId);
                    window.location.href = sessionId;
                } else {
                    await stripe.redirectToCheckout({
                        sessionId: sessionId
                    });
                }
            });
        } else {
            const voucherDto = {
                customerEmail: contactDetails.email,
                cancelUrl: `https://${host}/${encodeURIComponent(props.microsite)}/`,
                successUrl: setupContext.paymentProvider === 'GuestPay'
                    ? `https://${host}/${encodeURIComponent(props.microsite)}/Success`
                    : `https://${host}/${encodeURIComponent(props.microsite)}/Success?session_id={CHECKOUT_SESSION_ID}`,
                postageMethodIds: postageMethods,
                vouchersCheckedOut: vouchersToOrder
            };
            VoucherService.generateVoucherCheckoutSession(props.microsite, voucherDto).then(async function (sessionId) {
                storeCartInfoInLocalStorage(sessionId, vouchersToOrder);
                if (setupContext.paymentProvider === 'GuestPay') {
                    window.sessionStorage.setItem('guestpay_pending_session', sessionId);
                    window.location.href = sessionId;
                } else {
                    await stripe.redirectToCheckout({
                        sessionId: sessionId
                    });
                }
            });
        }

    }

    function createRequestObject(vouchersToOrder, contactDetails, host, postageMethods) {
        // TEMPORARY FIX: Double-encode microsite name to compensate for API's Uri type decoding.
        // The API's VoucherCheckoutWithOrderDetailsRequestDto uses Uri type for URLs, which decodes
        // URL-encoded characters. Double-encoding ensures Stripe receives properly encoded URLs.
        // REMOVE THIS after API fix is deployed (change Uri to string in DTO).
        const doubleEncodedMicrosite = encodeURIComponent(encodeURIComponent(props.microsite));

        return {
            vouchersOrdered: vouchersToOrder.map(vo => {
                return {
                    voucherId: vo.voucherId,
                    quantity: vo.quantity,
                    postageMethodId: vo.deliveryDetails.deliveryOption,
                    chargeForDelivery: vo.deliveryDetails.chargeForDelivery,
                    recipientDetails: {
                        fullName: `${vo.recipientDetails.firstName} ${vo.recipientDetails.lastName}`,
                        email: vo.recipientDetails.email,
                        message: vo.recipientDetails.message,
                        deliveryAddress: vo.deliveryMethod === deliveryMethod.delivery ? vo.deliveryDetails : null,
                    },
                    sendNotificationToRecipient: vo.deliveryMethod === deliveryMethod.email
                }
            }),
            billingDetails: {
                firstName: contactDetails.firstName,
                surname: contactDetails.lastName,
                email: contactDetails.email
            },
            sendNotificationToPurchaser: true,
            cancelUrl: `https://${host}/${doubleEncodedMicrosite}/`,
            successUrl: setupContext.paymentProvider === 'GuestPay'
                ? `https://${host}/${doubleEncodedMicrosite}/webhook-polling`
                : `https://${host}/${doubleEncodedMicrosite}/webhook-polling?session_id={CHECKOUT_SESSION_ID}`,
            postageMethodIds: postageMethods
        }
    }


    function storeCartInfoInLocalStorage(checkoutSessionId, vouchersOrdered) {
        const cartInfoToStore = {
            vouchersOrdered: vouchersOrdered,
            billingDetails: {
                firstName: contactDetails.firstName,
                lastName: contactDetails.lastName,
                email: contactDetails.email
            },
            totalCartPrice: props.totalCost
        }
        window.sessionStorage.setItem(checkoutSessionId, JSON.stringify(cartInfoToStore));
    }


    function contactEmailHasError() {
        return !ValidationHelper.isValidEmail(contactDetails.email);
    }

    function payWithCardButtonDisabled() {
        // Safety net: check if any "send to me" email delivery items lack recipient email
        const sendToMeItemsMissingEmail = props.orderItems.some(ci =>
            ci.sendingOption === sendingOption.sendToMe &&
            ci.deliveryMethod === deliveryMethod.email &&
            !ci.recipientDetails.email
        );

        return contactEmailHasError() ||
            !contactDetails.firstName ||
            !contactDetails.lastName ||
            !termsAndConditionsAccepted ||
            redirectingToCheckout ||
            sendToMeItemsMissingEmail ||
            (!venueTermsAndConditionsAccepted && setupContext.termsAndConditions);
    }

    return (
        <Fragment>
            <div className={isTabletView ? "cart-content-wrapper mobile" : "cart-content-wrapper"}>
                <div className="back-link d-flex" onClick={() => props.history.push(`/${props.microsite}/`)}>
                    <ChevronLeftIcon /><h5> <FormattedMessage id="Vouchers.SelectMoreVouchers" /></h5>
                </div>
                <div className={isTabletView ? "" : "d-flex"}>
                    <div className="contact-details">
                        <h4><FormattedMessage id="Vouchers.YourContactDetails" /></h4>
                        <Label><FormattedMessage id={"Common.FirstName"} /></Label>
                        <ValidationInput
                            type="text"
                            value={contactDetails.firstName}
                            onChange={(e) => setContactDetailsField("firstName", e)}
                            hasError={!contactDetails.firstName}
                            errorMessage={intl.formatMessage({ id: 'Vouchers.FieldNameRequired' },
                                {
                                    fieldName: intl.formatMessage({ id: 'Common.FirstName' })

                                }
                            )}
                        />
                        <Label><FormattedMessage id={"Common.LastName"} /></Label>
                        <ValidationInput
                            type="text"
                            value={contactDetails.lastName}
                            onChange={(e) => setContactDetailsField("lastName", e)}
                            hasError={!contactDetails.lastName}
                            errorMessage={intl.formatMessage({ id: 'Vouchers.FieldNameRequired' },
                                {
                                    fieldName: intl.formatMessage({ id: 'Common.LastName' })

                                }
                            )}
                        />
                        <Label><FormattedMessage id={"Common.EmailAddress"} /></Label>
                        <ValidationInput
                            type="email"
                            value={contactDetails.email}
                            onChange={(e) => setContactDetailsField("email", e)}
                            hasError={contactEmailHasError()}
                            errorMessage={intl.formatMessage({ id: 'Common.ValidEmailRequired' })}
                        />
                    </div>
                    <div className="order-summary">
                        <h4><FormattedMessage id="Vouchers.OrderSummary" /></h4>
                        {props.orderItems.length > 0 &&
                            <Fragment>
                                <div className="cart-items">
                                    {props.orderItems.map(cartItem =>
                                        <VoucherCartItem
                                            key={cartItem.voucherId}
                                            cartItem={cartItem}
                                            updateCartItemQuantity={updateCartItemQuantity}
                                            removeOrderItem={props.removeOrderItem}
                                            deliveryMethod={cartItem.deliveryDetails.deliveryOption ? setupContext.postageMethods.find(pm => pm.postageId === cartItem.deliveryDetails.deliveryOption) : null}
                                            isTabletView={isTabletView}
                                        />
                                    )}
                                </div>
                                <div className="total-price-wrapper">
                                    <h6 className="total"><FormattedMessage id={"Common.Total"} /> {setupContext.currencySymbol}{props.totalCost} </h6>
                                </div>
                                <div className="terms-and-conditions">
                                    <div className="d-flex">
                                        <Input
                                            id="terms-accepted"
                                            className="custom-control-cursor-pointer"
                                            type="checkbox"
                                            onChange={(e) => setTermsAndConditionsAccepted(e.target.checked)}
                                            checked={termsAndConditionsAccepted}
                                        />
                                        <div className="me-1">{intl.formatMessage({ id: 'Vouchers.AgreeToTermsAndConditions' })}</div>
                                        <ResdiaryTermsAndConditions />
                                    </div>
                                    { setupContext.termsAndConditions && (
                                        <div className="d-flex">
                                            <Input
                                                id="venue-terms-accepted"
                                                className="custom-control-cursor-pointer"
                                                type="checkbox"
                                                onChange={(e) => setVenueTermsAndConditionsAccepted(e.target.checked)}
                                                checked={venueTermsAndConditionsAccepted}
                                            />
                                            <div className="me-1">{intl.formatMessage({ id: 'Vouchers.AgreeToTermsAndConditions' })}</div>
                                            <RestaurantTermsAndConditions termsAndConditions={setupContext.termsAndConditions} />
                                        </div>
                                    )}
                                </div>
                                <ButtonWrapper
                                    text={redirectingToCheckout ? intl.formatMessage({ id: 'Common.Loading' }) : intl.formatMessage({ id: 'Common.PayWithCard' })}
                                    className="rd-button pay-with-card"
                                    onClick={() => redirectToCheckout()}
                                    disabled={payWithCardButtonDisabled()}
                                />
                            </Fragment>
                        }
                        {props.orderItems.length === 0 &&
                            <Button className="rd-button-outline select-voucher-button" onClick={() => props.history.push(`/${props.microsite}/`)}><FormattedMessage id={"Vouchers.SelectAVoucher"} /></Button>
                        }
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default injectIntl(VoucherCartPage);
